import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../css/photogallery.css';

const PhotoGallery = ({ images }) => {
  const carouselRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [arrowHovered, setArrowHovered] = useState(null);

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);

  const goPrev = useCallback(() => {
    if (lightbox === null) return;
    setLightbox((lightbox - 1 + images.length) % images.length);
  }, [lightbox, images.length]);

  const goNext = useCallback(() => {
    if (lightbox === null) return;
    setLightbox((lightbox + 1) % images.length);
  }, [lightbox, images.length]);

  // Keyboard nav (lightbox)
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'Escape')      closeLightbox();
      else if (e.key === 'ArrowLeft')  goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, goPrev, goNext]);

  // Body scroll lock while lightbox is open
  useEffect(() => {
    document.body.style.overflow = lightbox !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  // Carousel scroll
  const scrollCarousel = (dir) => {
    if (carouselRef.current) {
      const cardWidth = 380;
      carouselRef.current.scrollBy({ left: dir * cardWidth * 2, behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="photo-carousel-wrapper" id="photo-gallery">
        <button
          className="photo-carousel-arrow photo-carousel-arrow-left"
          onClick={() => scrollCarousel(-1)}
          onMouseEnter={() => setArrowHovered('left')}
          onMouseLeave={() => setArrowHovered(null)}
          style={{ transform: `translateY(-50%) scale(${arrowHovered === 'left' ? 1.2 : 1})` }}
          aria-label="Scroll left"
        >
          <i className="fas fa-chevron-left" />
        </button>

        <div ref={carouselRef} className="photo-carousel">
          {images.map((image, i) => (
            <div
              key={i}
              className="photo-carousel-item"
              onClick={() => openLightbox(i)}
            >
              <img
                src={image.url}
                alt={image.alt || `Screenshot ${i + 1}`}
                loading="lazy"
                className="photo-carousel-image"
              />
              {image.caption && (
                <div className="photo-carousel-caption">{image.caption}</div>
              )}
            </div>
          ))}
        </div>

        <button
          className="photo-carousel-arrow photo-carousel-arrow-right"
          onClick={() => scrollCarousel(1)}
          onMouseEnter={() => setArrowHovered('right')}
          onMouseLeave={() => setArrowHovered(null)}
          style={{ transform: `translateY(-50%) scale(${arrowHovered === 'right' ? 1.2 : 1})` }}
          aria-label="Scroll right"
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>

      <div className="photo-carousel-info">
        {images.length} screenshots • scroll or click any image to enlarge
      </div>

      {/* ── Lightbox overlay ─────────────────────────────────────────────── */}
      {lightbox !== null && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={images[lightbox]?.alt}
        >
          <button
            className="lightbox-arrow lightbox-prev"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous image"
          >&#8249;</button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[lightbox].url}
              alt={images[lightbox].alt}
              className="lightbox-img"
            />
            {images[lightbox].caption && (
              <div className="lightbox-caption">{images[lightbox].caption}</div>
            )}
            <div className="lightbox-counter">{lightbox + 1} / {images.length}</div>
          </div>

          <button
            className="lightbox-arrow lightbox-next"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next image"
          >&#8250;</button>

          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">✕</button>
        </div>
      )}
    </>
  );
};

export default PhotoGallery;
